import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { a11yProps, getMonthName } from '../../../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { actions } from '../../../../actions';
import collections from '../../../../constants/FirestoreCollections';
import esLocale from 'date-fns/locale/es';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFirestore } from 'react-redux-firebase';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { formatGestionName, getTime, getTimesArray } from '../../helpers';
import { tabsSalesCharts, tabsHealthDecisionCharts } from './IchartsSales';
import {
  IMPORTANT_CONSIDERATIONS_TEXT_SALES,
  IMPORTANT_CONSIDERATIONS_TEXT_HEALTHDECISION,
} from './constants';
import axios from 'axios';
import { handleErrorSwal, handleSuccesSwal, handleWarningSwal } from '../../../../shared/swals';

const useStyles = (small) => {
  return makeStyles((theme) => ({
    date: {
      width: '200px',
    },
  }));
};

const options = {
  indexAxis: 'x',
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    legend: {
      position: 'right',
    },
  },
};

function ChartsSales() {
  // define styles
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();

  // hooks
  const appDispatch = useDispatch();
  const organization = useSelector((state) => state.app.organization);
  const salesSoftware = organization?.services?.salesSoftware;
  const firestore = useFirestore();

  // states
  const [data, setData] = useState({});
  const [section, setSection] = useState(0);
  const [times, setTimes] = useState();
  const [date, setDate] = useState('');

  const [dailyData, setDailyData] = useState({});
  const [dailySection, setDailySection] = useState(1);
  const [dailyDate, setDailyDate] = useState('');
  const [initialDate, setInitialDate] = useState();
  const [endDate, setEndDate] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [refreshData, setRefreshData] = useState(true);
  const [lastManualUpdate, setLastManualUpdate] = useState();

  const tabsToShow = salesSoftware ? tabsSalesCharts : tabsHealthDecisionCharts;
  const textToShow = salesSoftware
    ? IMPORTANT_CONSIDERATIONS_TEXT_SALES
    : IMPORTANT_CONSIDERATIONS_TEXT_HEALTHDECISION;
  // methods

  const getLastManualUpdate = async () => {
    try {
      firestore
        .collection(collections.configurations)
        .where('name', '==', 'charts')
        .where('organizationId', '==', organization.id)
        .get()
        .then((res) => {
          if (!res.empty) {
            const config = res.docs[0].data();
            let lastUpdate = config.lastManualUpdate;
            if (!lastUpdate) {
              lastUpdate = {};
              lastUpdate.seconds = moment().subtract(4, 'hours').valueOf() / 1000;
            }
            setLastManualUpdate(lastUpdate);
          }
        });
    } catch (error) {
      console.error('getLastManualUpdate', error);
    }
  };

  const getNextUpdateHour = async () => {
    try {
      const nextUpdate = 3;
      const lastUpdateMoment = moment.unix(lastManualUpdate.seconds);
      // Obtener el momento actual
      const now = moment.utc();
      const differenceInMinutes = now.diff(lastUpdateMoment, 'minutes');
      // Evaluar si la diferencia es mayor o igual a 3 horas
      if (differenceInMinutes >= nextUpdate * 60) {
        return true;
      } else {
        const updateTargetTime = lastUpdateMoment.clone().add(3, 'hours');
        const targetTimeFormatted = updateTargetTime.format('HH:mm');
        return handleErrorSwal(`Podra actualizar nuevamente a las ${targetTimeFormatted}`).then(
          () => {
            return false;
          },
        );
      }
    } catch (error) {
      console.log('error en getNextUpdateHour', error);
    }
  };

  const fetchDataByTab = async ({ datePick, isDaily, sectionState }) => {
    appDispatch({
      type: actions.changeLoading,
      loading: { loading: true, priority: 2 },
    });
    firestore
      .collection(collections.configurations)
      .where('name', '==', 'charts')
      .where('organizationId', '==', organization.id)
      .get()
      .then(async (res) => {
        if (!res.empty) {
          const config = res.docs[0].data();
          const { dataName, typeValue, label, legend } =
            tabsToShow.find((tab) => tab.value === sectionState) || {};
          let _data;
          const chartTimes = getTimesArray(config[dataName]);
          let backgroundColor;
          let borderColor;
          switch (typeValue) {
            case 'YYYY-MM': {
              backgroundColor = 'rgba(54, 162, 235, 0.2)';
              borderColor = 'rgba(54, 162, 235, 1)';
              if (!datePick) {
                const momentDate = moment(chartTimes[0], 'YYYY-MM');
                setTimes(chartTimes);
                setDate(momentDate);
                return;
              }
              _data = config[dataName][datePick];
              break;
            }
            case 'YYYY-MM-DD': {
              setDisabled(false);
              backgroundColor = 'rgba(46, 139, 87, 0.2)'; // Verde
              borderColor = 'rgba(46, 139, 87, 1)'; // Verde
              if (!datePick) {
                const endDateTime = moment(chartTimes[chartTimes.length - 1], 'YYYY-MM-DD');
                const initialDateTime = moment(chartTimes[0], 'YYYY-MM-DD');
                setEndDate(endDateTime);
                setInitialDate(initialDateTime);
                setDailyDate(initialDateTime);
                return;
              }
              _data = config[dataName][datePick];
              break;
            }
            case '[0]': {
              setDisabled(true);
              backgroundColor = 'rgba(0, 0, 139, 0.2)'; // Azul oscuro
              borderColor = 'rgba(0, 0, 139, 1)'; // Azul oscuro
              _data = config[dataName].reverse();
              break;
            }
          }
          if (_data) {
            const tmpData = { ..._data };
            if (label === 'month') {
              tmpData.labels = _data.map((e) => getMonthName(e.month - 1));
            } else if (label === 'gestion') {
              tmpData.labels = _data.map((e) => formatGestionName(e[label]));
            } else {
              tmpData.labels = _data.map((e) => e[label]);
            }
            tmpData.datasets = [
              {
                label: legend,
                data: _data.map((e) => e.total),
                backgroundColor,
                borderColor,
                borderWidth: 1,
              },
            ];
            if (tmpData && isDaily) {
              setDailyData(tmpData);
            } else if (tmpData) {
              setData(tmpData);
            }
          } else {
            if (isDaily) {
              setDailyData([]);
              handleWarningSwal(`No hay informacion para mostrar en la fecha ${datePick}`);
            }
          }
        }
      })
      .catch((e) => {
        console.log('error en Charts', e);
      });

    appDispatch({
      type: actions.changeLoading,
      loading: { loading: false, priority: 2 },
    });
  };

  // Funcion para llamar al endpoint encargado de actualizar los charts:
  // Top Gestiones por dia, Asistencia Manual Diaria, Chats Diarios por mes
  const handleUpdateData = async () => {
    try {
      const canUpdate = await getNextUpdateHour();
      if (canUpdate) {
        appDispatch({
          type: actions.changeLoading,
          loading: { loading: true, priority: 2 },
        });
        await axios
          .post(`${process.env.REACT_APP_EXPORT_KPI}`, {
            type: 'updateDailyCharts',
            organizationId: organization.id,
            flags: { salesSoftware },
          })
          .then(async () => {
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: false, priority: 2 },
            });
            setRefreshData(!refreshData);
            handleSuccesSwal('Datos Actualizados');
          })
          .catch(async (error) => {
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: false, priority: 2 },
            });
            handleErrorSwal();
            console.error(error);
          });
      }
    } catch (err) {
      console.log('Error en handleUpdateData', err);
    }
  };

  // effects

  useEffect(() => {
    getLastManualUpdate();
  }, []);

  useEffect(() => {
    getLastManualUpdate();
  }, [refreshData]);

  useEffect(() => {
    fetchDataByTab({ sectionState: section });
  }, [appDispatch, section, organization.id, refreshData]);

  useEffect(() => {
    fetchDataByTab({ isDaily: true, sectionState: dailySection });
  }, [appDispatch, dailySection, organization.id, refreshData]);

  useEffect(() => {
    if (date !== '') {
      const formattedDate = moment(date).format('YYYY-MM');
      fetchDataByTab({ datePick: formattedDate, sectionState: section });
    }
  }, [date]);

  useEffect(() => {
    if (dailyDate !== '') {
      const formattedDate = moment(dailyDate).format('YYYY-MM-DD');
      fetchDataByTab({
        datePick: formattedDate,
        isDaily: true,
        sectionState: dailySection,
      });
    }
  }, [dailyDate]);

  return (
    <Box>
      <Box mb={4} alignItems="center">
        <Typography variant="h6">Consideraciones Importantes:</Typography>
        <Typography
          variant="body1"
          sx={{ mb: 2 }}
          dangerouslySetInnerHTML={{
            __html: textToShow,
          }}
        />
      </Box>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <FormControl fullWidth className={classes.date}>
          <InputLabel>Seleccione período</InputLabel>
          <Select
            value={date !== '' ? date.format('YYYY-MM') : date}
            label="Seleccione período"
            onChange={(e) => {
              setDate(moment(new Date(`${e.target.value.replace('-', '/')}/01`)));
            }}
          >
            {times &&
              times.map((time) => (
                <MenuItem className={classes.selectItem} key={time} value={time}>
                  {getTime(time)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button
          startIcon={<Refresh />}
          variant="contained"
          color="primary"
          onClick={() => {
            handleUpdateData();
          }}
        >
          Actualizar Datos
        </Button>
      </Box>
      <Tabs
        value={section}
        onChange={(_, newValue) => setSection(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="scrollable auto tabs example"
      >
        {tabsToShow.map(
          (tab, index) =>
            tab.typeValue === 'YYYY-MM' && (
              <Tab label={tab.name} value={tab.value} {...a11yProps(index)} key={index} />
            ),
        )}
      </Tabs>

      <Box display="flex" justifyContent="center" className={classes.chart} mt={1}>
        <Box width="100%">
          {data?.datasets?.length > 0 && <Bar data={data} options={options} redraw />}
        </Box>
      </Box>
      <Box className={classes.date} mb={4} mt={4}>
        <LocalizationProvider adapterLocale={esLocale} dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            inputFormat="dd/MM/yyyy"
            value={dailyDate}
            maxDate={initialDate}
            minDate={endDate}
            onChange={(date) => {
              setDailyDate(moment.utc(date));
            }}
            renderInput={(params) => <TextField {...params} />}
            disableFuture
            disabled={disabled}
          />
        </LocalizationProvider>
      </Box>
      <Tabs
        value={dailySection}
        onChange={(_, newValue) => setDailySection(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="scrollable auto tabs example"
      >
        {tabsToShow.map(
          (tab, index) =>
            tab.typeValue !== 'YYYY-MM' && (
              <Tab label={tab.name} value={tab.value} {...a11yProps(index)} key={index} />
            ),
        )}
      </Tabs>
      <Box display="flex" justifyContent="center" className={classes.chart} mt={1}>
        <Box width="100%">
          {dailyData?.datasets?.length > 0 && <Bar data={dailyData} options={options} redraw />}
        </Box>
      </Box>
    </Box>
  );
}

export default ChartsSales;
